import Parse from "parse";
import React, { ReactNode, useContext, useEffect, useState } from "react";
import { useAuth } from "../components/auth/ParseAuthContext.tsx";
import { FeatureFlags, noFeatures } from "../shared/featureFlags";

// undefined until the server has answered for the logged-in user
export const FeatureFlagsContext = React.createContext<
  FeatureFlags | undefined
>(undefined);

// Asks the server for the logged-in user's feature toggles: when they log in,
// and again whenever they are re-read, since the master key can change the
// toggles of a user who is logged in.
export const FeatureFlagsProvider = ({
  children,
}: {
  children: ReactNode | ReactNode[];
}) => {
  const { auth } = useAuth();
  const user = auth.kind === "signedIn" ? auth.user : undefined;
  const [answer, setAnswer] = useState<{
    username: string;
    flags: FeatureFlags;
  }>();

  useEffect(() => {
    if (!user) return;
    const { username } = user;
    let cancelled = false;
    Parse.Cloud.run("getFeatureFlags")
      .catch((error) => {
        console.warn("Could not load feature flags", error);
        // nothing is switched on for a user the server did not answer for
        return noFeatures();
      })
      .then((flags: FeatureFlags) => {
        if (!cancelled) setAnswer({ username, flags });
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  // an answer given for someone else says nothing about this user
  const flags =
    answer && answer.username === user?.username ? answer.flags : undefined;

  return (
    <FeatureFlagsContext.Provider value={flags}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

// The logged-in user's feature toggles, or undefined while the server has not
// answered yet. A caller for whom "off" and "not known yet" differ must wait.
export function useFeatureFlags(): FeatureFlags | undefined {
  return useContext(FeatureFlagsContext);
}
